import { FC, Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isNil, range } from 'ramda';
import { Box, Button, Flex, SxStyleProp, Text } from 'theme-ui';
import { getDuration } from 'modules/scenes';
import useToggle from 'react-use/lib/useToggle';
import useAsync from 'react-use/lib/useAsync';
import { getAudio, setAudio, setAudioRange } from 'modules/audio';
import Icon, { IconTypes } from 'shared/Icon';
import { AudioTrimModal } from './AudioTrimModal';
import { getFile } from 'utils/fileCache';
import { cancelUploadUserSong, isUserSongUploading } from 'modules/userSongs';
import analytics from 'utils/analytics';
import Tooltip, { placementsList } from '@picsart/rc/old/Tooltip';
import { saveRevision } from 'modules/revisions';
import { ToolName } from 'types/AppState';
import { useTranslator } from '@picsart/rc/services/localization';
import { usePlayer } from 'utils/PlayerContext';

type AudioTimeLineProps = {
  videoDuration: number;
  duration: number;
  loading: boolean;
};

const defaultTrackStyle: SxStyleProp = {
  height: '2px',
  bg: 'lilac',
  opacity: 0.6
};
const iconContainer: SxStyleProp = {
  ml: 2,
  button: {
    p: 0
  },
  svg: {
    height: 16,
    fill: 'grey7',
    '&:hover': {
      fill: 'grey6'
    }
  }
};
const audioLabel: SxStyleProp = {
  mb: 0,
  color: 'lilac',
  fontWeight: 'bold',
  fontSize: 1,
  width: 96,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const audioTrackPreviewStyle = (width: number, isPresent: boolean): SxStyleProp => ({
  ...defaultTrackStyle,
  width: `${width}%`,
  opacity: isPresent ? 1 : 0.4
});

export const AudioTimeLine: FC<AudioTimeLineProps> = ({ duration, videoDuration, loading }) => {
  const [trackPreviewWidth, setTrackPreviewWidth] = useState(100);

  useEffect(() => {
    if (duration !== 0) {
      const width = videoDuration <= duration ? 100 : (duration * 100) / videoDuration;
      setTrackPreviewWidth(width);
    }
  }, [duration, videoDuration]);

  return (
    <Fragment>
      {!loading ? (
        <Flex sx={{ '&: last-child': { ml: 0, borderRadius: 0 } }}>
          <Box sx={audioTrackPreviewStyle(trackPreviewWidth, duration !== 0)} />
          {range(1, Math.floor(100 / trackPreviewWidth)).map(item => (
            <Box
              key={item}
              sx={{ ...audioTrackPreviewStyle(trackPreviewWidth, false), ml: '2px' }}
            />
          ))}
          {!!(100 % trackPreviewWidth) && (
            <Box bg="grey2" sx={{ ...defaultTrackStyle, flexGrow: 1, ml: '2px', opacity: 0.4 }} />
          )}
        </Flex>
      ) : (
        <Box sx={audioTrackPreviewStyle(100, false)} />
      )}
    </Fragment>
  );
};

export const AudioPanel = () => {
  const [isTrimShown, toggleIsTrimShown] = useToggle(false);
  const song = useSelector(getAudio);
  const { url, songName, artistName, trimStart, trimDuration, duration } = song || {};
  const { loading } = useAsync(async () => (url ? getFile(url) : null), [url]);
  const userSongUploading = useSelector(isUserSongUploading);
  const translator = useTranslator();
  const { pause } = usePlayer();

  const dispatch = useDispatch();
  const videoDuration = useSelector(getDuration) / 1000;

  if (isNil(url) && !userSongUploading) {
    return null;
  }
  const deleteAudio = () => {
    analytics.musicAction({ action: 'remove' });
    dispatch(cancelUploadUserSong());
    dispatch(setAudio(null));
    dispatch(saveRevision(ToolName.Music));
  };
  const showAudioTrimModal = () => {
    pause();
    toggleIsTrimShown(true);
  };
  const hideAudioTrimModal = () => toggleIsTrimShown(false);
  const applyTrimHandler = (start: number, songDuration: number) => {
    analytics.trimAction({
      left_moved: typeof trimStart !== 'undefined' && start !== trimStart,
      right_moved: typeof trimDuration !== 'undefined' && songDuration !== trimDuration,
      media_type: 'audio'
    });
    dispatch(setAudioRange(start, songDuration));
    dispatch(saveRevision(ToolName.Trim));
  };
  let title = '';
  let userDuration = 0;
  if (!isNil(song)) {
    title = artistName ? `${artistName} - ${songName}` : songName;
    userDuration = (trimDuration || duration) / 1000;
  }
  return (
    <Fragment>
      <Box sx={{ position: 'absolute', width: '100%', top: '-4px' }}>
        <Flex sx={{ mb: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
          <Text variant="body" sx={{ mr: 1, mb: 0, color: 'grey6', fontSize: 1 }}>
            {translator('Audio')}:
          </Text>
          <Tooltip content={title} placement={placementsList.top}>
            <Text variant="body" sx={audioLabel} data-test="audio_song_title">
              {loading || userSongUploading ? `${translator('Loading')}...` : title}
            </Text>
          </Tooltip>
          {userDuration <= videoDuration && (
            <Tooltip content={translator('This_track_is_looped')} placement={placementsList.top}>
              <span>
                <Icon
                  name="loop-editor"
                  sx={{ mt: '2px', width: '20px', height: '20px' }}
                  type={IconTypes.RatioFormat}
                />
              </span>
            </Tooltip>
          )}
          <Flex sx={iconContainer}>
            {!loading && !userSongUploading && (
              <Tooltip content={translator('Trim_Audio')} placement={placementsList.top}>
                <Button variant="text" data-test="trim_audio" onClick={showAudioTrimModal}>
                  <Icon name="trim-editor" sx={{ width: '20px', height: '20px' }} />
                </Button>
              </Tooltip>
            )}
            <Tooltip content={translator('Delete_Audio')} placement={placementsList.topEnd}>
              <Button variant="text" sx={{ ml: 1 }} data-test="delete_audio" onClick={deleteAudio}>
                <Icon sx={{ width: '20px', height: '20px' }} name="delete-editor" />
              </Button>
            </Tooltip>
          </Flex>
        </Flex>
        <AudioTimeLine
          videoDuration={videoDuration}
          duration={userDuration}
          loading={loading || userSongUploading}
        />
      </Box>

      <AudioTrimModal
        applyHandler={applyTrimHandler}
        opened={isTrimShown}
        close={hideAudioTrimModal}
        src={url}
        title={title}
        trimStart={trimStart}
        trimDuration={trimDuration}
        duration={duration}
      />
    </Fragment>
  );
};
